import { GenericTeamInterface } from '../types';
import { keyBy } from 'lodash';

/**
 * A utility function that takes an array of teams
 * And added targetServices list to the team,
 * The team also exists on BE, but not all teams have integrations
 * Thus, this function should only be used for teams that are fetched from IS
 * (i.e: fetched from service metrics endpoints)
 * The goal is to avoid calling IS API with all the teams that doesn't have integration
 * @returns {Record<number,object>}
 */
export const makeIntegrationTeamInfoMappings = (
  teams: GenericTeamInterface[],
  targetService: string,
): Record<number, object> => {
  const integrationTeamsInfoMapping = teams.map((team) => {
    // The team can have more than 1 type of target service
    if (!team.targetServices) {
      return {
        ...team,
        targetServices: [targetService],
      };
    }
    const targetServiceSet = new Set(team.targetServices);

    return {
      ...team,
      targetServices: targetServiceSet.has(targetService)
        ? team.targetServices
        : [...team.targetServices, targetService],
    };
  });
  return keyBy(integrationTeamsInfoMapping, 'id');
};

export const getTeamsWithIntegration = (teams: GenericTeamInterface[]) => {
  return teams.filter((team) => team.targetServices);
};

// Rejection sampling avoids the modulo bias a bare `% max` would introduce when max doesn't divide 2^32.
const randomIndex = (max: number): number => {
  const limit = Math.floor(2 ** 32 / max) * max;
  const buffer = new Uint32Array(1);
  let value: number;
  do {
    crypto.getRandomValues(buffer);
    value = buffer[0];
  } while (value >= limit);
  return value % max;
};

export const generateStrongPassword = () => {
  const lowercaseChars = 'abcdefghijklmnopqrstuvwxyz';
  const uppercaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';
  const specialChars = '!@#$%^&*()_+-=[]{}|;:,.<>?';

  const getRandomChar = (charSet: string | any[]) => {
    return charSet[randomIndex(charSet.length)];
  };

  let password = '';

  // Add at least one lowercase letter
  password += getRandomChar(lowercaseChars);

  // Add at least one uppercase letter
  password += getRandomChar(uppercaseChars);

  // Add at least one number
  password += getRandomChar(numbers);

  // Add at least one special character
  password += getRandomChar(specialChars);

  const remainingLength = 14 - password.length;

  // Add remaining random characters
  for (let i = 0; i < remainingLength; i++) {
    const charSet = lowercaseChars + uppercaseChars + numbers + specialChars;
    password += getRandomChar(charSet);
  }

  // Fisher-Yates shuffle: every permutation is equally likely, unlike a comparator-based sort.
  const chars = password.split('');
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomIndex(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
};
